/**
 * Schémas Zod — Extractions, anomalies, corrections.
 */

import { z } from "zod";

import { SEVERITIES } from "../constants.js";

export const ExtractedFieldSchema = z.object({
  field_key: z.string().min(1).max(120),
  value: z.unknown(),
  unit: z.string().max(20).optional(),
  confidence: z.number().min(0).max(1).optional(),
  agreement: z.number().min(0).max(1).optional(),
  needs_review: z.boolean().default(false),

  citation_file_id: z.string().uuid().optional(),
  citation_page: z.number().int().positive().optional(),
  citation_quote: z.string().max(2000).optional(),
  citation_bbox: z
    .object({
      x: z.number(),
      y: z.number(),
      w: z.number(),
      h: z.number(),
    })
    .optional(),
});
export type ExtractedField = z.infer<typeof ExtractedFieldSchema>;

// -----------------------------------------------------------------------------
// Note D23 (résolu — Chunk 9.A)
// -----------------------------------------------------------------------------
// Auparavant ce fichier exposait `StartExtractionSchema` :
//
//   export const StartExtractionSchema = z.object({
//     submission_id: z.string().uuid(),
//     passes: z.array(z.string()).optional(),
//     model_id: z.string().optional(),
//   });
//
// Ce schéma était spéculatif et n'était consommé par AUCUN code (ni l'API,
// ni le frontend, ni MCP). L'API définit son body schema localement dans
// `apps/api/src/routes/extractions.ts` (`StartExtractionBodySchema`) avec
// la forme réelle `{ submission_id, file_id, pass_id?, model_id? }` —
// décision architecturale assumée ("internes au boundary HTTP, pas
// réutilisés ailleurs").
//
// Supprimé en 9.A pour éliminer une 3ème variante divergente du même
// concept et clarifier le rôle de @kollectivia/shared : n'exporter que
// les schémas réellement consommés ≥2 fois (API + web ou API + MCP).
// -----------------------------------------------------------------------------

export const SubmitCorrectionSchema = z.object({
  submission_id: z.string().uuid(),
  field_key: z.string().min(1),
  corrected_value: z.unknown(),
  citation_quote: z.string().max(2000).optional(),
  citation_page: z.number().int().positive().optional(),
  note: z.string().max(2000).optional(),
});
export type SubmitCorrectionInput = z.infer<typeof SubmitCorrectionSchema>;

export const CreateAnomalySchema = z.object({
  submission_id: z.string().uuid().optional(),
  project_id: z.string().uuid().optional(),
  type: z.string().min(2).max(80),
  severity: z.enum(SEVERITIES).default("warn"),
  message: z.string().min(2).max(1000),
  details: z.record(z.unknown()).optional(),
  field_keys: z.array(z.string()).optional(),
  detected_by: z.string().max(80).optional(),
});

export const ResolveAnomalySchema = z.object({
  resolution_note: z.string().max(2000).optional(),
});

// -----------------------------------------------------------------------------
// 9.T.5.v3.1 — Extraction source discriminée (ADR 0029 Q1 EX-B)
// -----------------------------------------------------------------------------
//
// L'infrastructure d'extraction supporte maintenant 2 types de sources parent :
//   - `submission` (comportement existant, backward compat 100%)
//   - `intake_event` (nouveau — extraction structurée pré-materialize)
//
// Le type `ExtractionSource` est **discriminé strict** pour éviter toute
// ambiguïté au niveau du service interne. Contrat backend :
//
//   type ExtractionSource =
//     | { kind: "submission"; id: string }
//     | { kind: "intake_event"; id: string };
//
// Le wire HTTP accepte 2 formes explicites (audit PO V3.1-Q2) — voir
// `StartExtractionBodySchema` côté route API :
//   - Legacy : `{ submission_id, file_id, ... }` (backward compat 100%)
//   - Nouvelle : `{ source: { kind, id }, file_id, ... }`
//   - Interdit : mélange `source` + `submission_id` / `intake_event_id`
// -----------------------------------------------------------------------------

export const ExtractionParentKindSchema = z.enum(["submission", "intake_event"]);
export type ExtractionParentKind = z.infer<typeof ExtractionParentKindSchema>;

export const ExtractionSourceSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("submission"),
    id: z.string().uuid(),
  }),
  z.object({
    kind: z.literal("intake_event"),
    id: z.string().uuid(),
  }),
]);
export type ExtractionSource = z.infer<typeof ExtractionSourceSchema>;

// -----------------------------------------------------------------------------
// 9.T.5.v3.3 — Wire schemas parent-agnostiques (shared source of truth)
// -----------------------------------------------------------------------------
//
// Décisions PO 2026-07-14 :
//   Q3 refacto propre : les schemas HTTP `OverrideExtractedField`,
//     `ExtractedFieldRow`, `ExtractionRow`, `StartExtractionBody` étaient
//     dupliqués backend local + web queries. Consolidés ici comme source
//     unique de vérité (parity ADR pattern `SchemasNS`).
//   Q5 hard-break : la signature `useOverrideExtractedField` passe de
//     `{ submissionId }` à `{ parentKind, parentId }` — les schemas ci-dessous
//     n'ont plus AUCUNE référence submission-spécifique.
// -----------------------------------------------------------------------------

/**
 * Row `app.extracted_fields` — projection `EXTRACTED_FIELD_COLS` du helper
 * `apps/api/src/lib/extraction-helpers.ts:25-29`. Parent-agnostic : les 2
 * champs `submission_id | intake_event_id` sont mutex (CHECK DB `num_nonnulls=1`,
 * cf. migration 0047 v3.1).
 */
export const ExtractedFieldRowSchema = z.object({
  id: z.string().uuid(),
  tenant_id: z.string().uuid(),
  submission_id: z.string().uuid().nullable(),
  intake_event_id: z.string().uuid().nullable(),
  field_key: z.string().min(1).max(120),
  value: z.unknown(),
  unit: z.string().nullable(),
  confidence: z.number().min(0).max(1).nullable(),
  agreement: z.number().min(0).max(1).nullable(),
  needs_review: z.boolean(),
  citation_file_id: z.string().uuid().nullable(),
  citation_page: z.number().int().positive().nullable(),
  citation_quote: z.string().nullable(),
  citation_bbox: z
    .object({
      x: z.number(),
      y: z.number(),
      w: z.number(),
      h: z.number(),
    })
    .nullable(),
  extraction_id: z.string().uuid().nullable(),
  source_model_id: z.string().nullable(),
  override_by: z.string().uuid().nullable(),
  overridden_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type ExtractedFieldRow = z.infer<typeof ExtractedFieldRowSchema>;

/**
 * Codes d'échec d'extraction — **énumération fermée, source de vérité unique**
 * (chunk 9.W.2, décision PO §2).
 *
 * Remplace l'ancienne colonne `error_message` qui recevait `err.message` brut :
 * message du fournisseur d'IA, trace technique, voire fragment du document
 * client — le tout projeté au wire et affiché verbatim par le frontend.
 *
 * Contrat :
 *   - **Seule** surface affichable d'un échec d'extraction. Le frontend localise
 *     ces codes via ses catalogues i18n (`submissions.extraction.error_codes.*`),
 *     JAMAIS par regex ni `includes()` sur un texte.
 *   - Aucune contrainte `CHECK` en base : ce schéma est l'autorité (cf. 0061).
 *   - Aucune PII possible par construction.
 *
 * Distinction volontaire `PROVIDER_UNAVAILABLE` / `PROVIDER_CONFIGURATION` :
 * le premier est transitoire (réessayer a du sens), le second est définitif
 * (crédit, clé, permission — seule une intervention opérateur débloque).
 */
export const ExtractionErrorCodeSchema = z.enum([
  /** Budget total dépassé — le fournisseur n'a pas répondu à temps. */
  "EXTRACTION_TIMEOUT",
  /** 429 / 529 / 5xx après retentatives bornées. Transitoire. */
  "EXTRACTION_PROVIDER_UNAVAILABLE",
  /** Refus définitif du fournisseur : solde de crédit, clé, permission. */
  "EXTRACTION_PROVIDER_CONFIGURATION",
  /** Réponse reçue mais non exploitable (JSON invalide ou shape non conforme). */
  "EXTRACTION_INVALID_OUTPUT",
  /** Échec interne hors fournisseur : Storage, PDF illisible, écriture DB. */
  "EXTRACTION_INTERNAL_ERROR",
  /** Job `running` abandonné (redémarrage process), récupéré par le reaper. */
  "EXTRACTION_STALE_REAPED",
  /** Échec antérieur à 9.W.2, normalisé par 0061 — cause d'origine non conservée. */
  "EXTRACTION_LEGACY_FAILURE",
]);
export type ExtractionErrorCode = z.infer<typeof ExtractionErrorCodeSchema>;

/** Liste — utilisée par les tests d'exhaustivité i18n côté frontend. */
export const ALL_EXTRACTION_ERROR_CODES = ExtractionErrorCodeSchema.options;

/**
 * Row `app.extractions` — projection `EXTRACTION_COLS` du helper (v3.1 étendu
 * avec `intake_event_id` nullable). Aucune référence submission-only.
 */
export const ExtractionRowSchema = z.object({
  id: z.string().uuid(),
  tenant_id: z.string().uuid(),
  submission_id: z.string().uuid().nullable(),
  intake_event_id: z.string().uuid().nullable(),
  model_id: z.string().nullable(),
  pass_id: z.string().nullable(),
  status: z.enum(["pending", "running", "done", "failed"]),
  // 9.X.2 — `raw_response` RETIRÉE du contrat wire.
  //
  // La colonne existe toujours en base (`sensitive_legal` / `preserve`, 0047) et
  // continue d'être alimentée pour l'audit forensic, mais elle n'est plus projetée
  // par aucune route : elle transportait la sortie brute du modèle sur un document
  // client — texte verbatim en cas d'échec de parse, JSON structuré incluant des
  // `citation` (extraits directs du PDF) en cas de succès.
  //
  // Aucun champ optionnel, aucun alias déprécié, aucune phase de transition : le
  // schéma représente le contrat wire ACTUEL. Un consommateur qui aurait besoin
  // de cette donnée doit passer par les outils SQL autorisés, pas par l'API.
  prompt_tokens: z.number().int().nullable(),
  completion_tokens: z.number().int().nullable(),
  cost_usd: z.number().nullable(),
  duration_ms: z.number().int().nullable(),
  /**
   * 9.W.2 — DÉPRÉCIÉE comme surface affichable. Conservée pour compatibilité de
   * lecture ; normalisée à NULL par 0061 et plus jamais alimentée. Le frontend
   * ne doit PAS l'afficher : utiliser `error_code`.
   */
  error_message: z.string().nullable(),
  /**
   * 9.W.2 — code d'échec structuré, seule surface affichable. `passthrough` du
   * type fermé : une valeur inconnue (ligne écrite par une version antérieure)
   * est tolérée en lecture pour ne jamais casser un GET, mais le frontend la
   * traite comme un échec générique.
   */
  error_code: ExtractionErrorCodeSchema.or(z.string()).nullable(),
  started_at: z.string().nullable(),
  completed_at: z.string().nullable(),
  created_at: z.string(),
});
export type ExtractionRow = z.infer<typeof ExtractionRowSchema>;
export type ExtractionStatus = ExtractionRow["status"];

/**
 * Body PATCH `/v1/extracted-fields/:id` — override humain, gated user-only
 * (l'auth doit être un user, pas une api_key, cf. gating.ts + route handler).
 *
 * Un champ de provenance omis (`confidence`, `citation_quote`, `citation_page`)
 * conserve sa valeur ; une valeur ou null explicite garde la sémantique du PATCH.
 * D38 : ces champs ne sont pas éditables dans l'interface. Le hook web n'envoie
 * que `value`, `unit`, `needs_review`, `note`. L'override reste réservé aux users.
 */
export const OverrideExtractedFieldSchema = z.object({
  value: z.unknown(),
  unit: z.string().max(20).nullable().optional(),
  confidence: z.number().min(0).max(1).nullable().optional(),
  citation_quote: z.string().max(2000).nullable().optional(),
  citation_page: z.number().int().positive().nullable().optional(),
  needs_review: z.boolean().optional(),
  note: z.string().max(2000).nullable().optional(),
});
export type OverrideExtractedFieldInput = z.infer<typeof OverrideExtractedFieldSchema>;

/**
 * Body POST `/v1/extractions` — union stricte (v3.1 audit PO Q2).
 * Refuse les formes ambiguës (`source` + `submission_id` combinés → 400).
 */
export const StartExtractionLegacyBodySchema = z
  .object({
    submission_id: z.string().uuid(),
    file_id: z.string().uuid(),
    pass_id: z.string().min(2).max(40).optional(),
    model_id: z.string().min(3).max(100).optional(),
  })
  .strict();

export const StartExtractionSourceBodySchema = z
  .object({
    source: ExtractionSourceSchema,
    file_id: z.string().uuid(),
    pass_id: z.string().min(2).max(40).optional(),
    model_id: z.string().min(3).max(100).optional(),
  })
  .strict();

export const StartExtractionBodySchema = z.union([
  StartExtractionLegacyBodySchema,
  StartExtractionSourceBodySchema,
]);
export type StartExtractionBody = z.infer<typeof StartExtractionBodySchema>;

// -----------------------------------------------------------------------------
// 9.AA.1a — orchestration des passes métier
// -----------------------------------------------------------------------------

/**
 * Query `GET /v1/extraction-orchestrations/plan` — prévisualisation.
 *
 * `.strict()` volontaire : un paramètre inconnu est une erreur d'appel, pas une
 * option à ignorer. Le plan ne dépend que de la soumission — il se calcule depuis
 * `sections_present`, produit par la passe `identification`, sans lire le fichier
 * ni appeler le fournisseur. Prévisualiser ne coûte donc rien.
 */
export const ExtractionPlanQuerySchema = z
  .object({
    submission_id: z.string().uuid(),
  })
  .strict();
export type ExtractionPlanQuery = z.infer<typeof ExtractionPlanQuerySchema>;

/**
 * Body `POST /v1/extraction-orchestrations` — lancement.
 *
 * `file_id` est EXIGÉ, jamais déduit : c'est lui qui détermine le document envoyé
 * au fournisseur, donc la dépense engagée. Le déduire d'une soumission qui porte
 * plusieurs fichiers ferait extraire le mauvais document en silence.
 */
export const StartExtractionOrchestrationSchema = z
  .object({
    submission_id: z.string().uuid(),
    file_id: z.string().uuid(),
    model_id: z.string().min(3).max(100).optional(),
  })
  .strict();
export type StartExtractionOrchestrationInput = z.infer<typeof StartExtractionOrchestrationSchema>;

/**
 * Query `GET /v1/extraction-orchestrations` — historique d'une soumission.
 *
 * Sert la reprise d'état après rafraîchissement (9.AA.1b) : l'UI doit pouvoir
 * retrouver la dernière orchestration, y compris TERMINÉE, pour réafficher son
 * bilan. `/plan` n'expose que celle en cours.
 *
 * `limit` est borné bas (10 par défaut, 50 au plus) : l'interface n'a besoin que
 * des plus récentes, et un plafond évite qu'une soumission relancée cinquante fois
 * ne renvoie tout son historique à chaque montage du panneau.
 */
export const ExtractionOrchestrationListQuerySchema = z
  .object({
    submission_id: z.string().uuid(),
    limit: z.coerce.number().int().min(1).max(50).default(10),
  })
  .strict();
export type ExtractionOrchestrationListQuery = z.infer<
  typeof ExtractionOrchestrationListQuerySchema
>;
